import Editor from "@monaco-editor/react";
import { Close as CloseIcon, Save as SaveIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Logger from "../../services/Logger";

/**
 * Reusable dialog to edit filledCoordinates JSON.
 * Used by both grid and crossword template detail pages.
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Called when dialog closes
 * @param {Array} initialData - Parsed filledCoordinates array (e.g. from template)
 * @param {function} onSave - Async callback(data) called with parsed array on save
 */
export default function EditFilledCoordinatesDialog({ open, onClose, initialData, onSave }) {
    const [jsonValue, setJsonValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            try {
                const data = initialData ?? [];
                const str = Array.isArray(data)
                    ? JSON.stringify(data, null, 2)
                    : JSON.stringify(data, null, 2);
                setJsonValue(str);
                setError(null);
            } catch (e) {
                Logger.error("EditFilledCoordinatesDialog: failed to serialize initialData", e);
                setJsonValue("[]");
                setError("Invalid initial data");
            }
        } else {
            setJsonValue("");
            setError(null);
        }
    }, [open, initialData]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            let parsed;
            try {
                parsed = JSON.parse(jsonValue || "[]");
            } catch (parseError) {
                setError("Invalid JSON. Please check the syntax.");
                setSaving(false);
                return;
            }

            if (!Array.isArray(parsed)) {
                setError("Data must be a JSON array.");
                setSaving(false);
                return;
            }

            if (onSave) {
                await onSave(parsed);
            }
            onClose();
        } catch (err) {
            Logger.error("EditFilledCoordinatesDialog: save failed", err);
            setError(err?.message || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: "480px"
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6" component="span">
                        Edit filledCoordinates
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        disabled={saving}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.action.hover
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Edit the filledCoordinates JSON below. Each entry typically has{" "}
                    <code>positions</code>, <code>text</code>, and <code>direction</code>.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    sx={{
                        border: error ? "1px solid #d32f2f" : "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                        height: "380px"
                    }}
                >
                    <Editor
                        height="380px"
                        defaultLanguage="json"
                        value={jsonValue}
                        onChange={(value) => {
                            setJsonValue(value || "");
                            setError(null);
                        }}
                        theme="vs-light"
                        options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            fontSize: 14,
                            wordWrap: "on",
                            formatOnPaste: true,
                            formatOnType: true,
                            tabSize: 2,
                            automaticLayout: true
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                >
                    {saving ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
